"use client";

import { SectionHeader } from "@/components/common/section-header";
import { Box } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import FaqSectionComponent from "@/components/common/faq/faq";
import { FAQSection } from "@/types/dto/destinations-page/destination-page-types";
import { BasicInfo } from "@/types/dto/common/basic-info";
import { useTranslations } from "next-intl";
import useKeyInformation from "@/hooks/product-detail-page/key-information/use-key-information";
import { useMarketCurrency } from "@/providers/market-currency-provider";
import KeyInformationSkeleton from "./key-information-skeleton";

interface KeyInformationProps {
  packageId: string;
  neoId: string;
  keyInfoHeader?: BasicInfo;
  sectionId?: string | null;
}

const KeyInformation = ({
  packageId,
  neoId,
  keyInfoHeader,
  sectionId
}: KeyInformationProps) => {
  const { currencyId, marketId } = useMarketCurrency();
  const { data: keyInformationData, isLoading } = useKeyInformation(
    packageId,
    neoId,
    currencyId,
    marketId,
    !!currencyId && !!marketId
  );
  const t = useTranslations("PDP");
  const resolvedSectionId =
    sectionId === undefined ? "essential-tour-information" : sectionId;
  const hasScrolledToKeyInformation = useRef(false);

  // Extract href values from copyText using regex and memoize the result
  const hrefs = useMemo(
    () =>
      Array.from(keyInfoHeader?.copyText?.matchAll(/href="([^"]*)"/g) ?? [], (m: RegExpExecArray) =>
        m[1].startsWith("#") ? m[1].slice(1) : m[1]
      ),
    [keyInfoHeader?.copyText]
  );

  useEffect(() => {
    let animationFrameId = 0;
    let correctionTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let allowLayoutCorrections = true;

    const stopLayoutCorrections = () => {
      allowLayoutCorrections = false;
      if (correctionTimeoutId) clearTimeout(correctionTimeoutId);
    };

    const scrollToSection = (el: HTMLElement) => {
      const scrollMarginTop = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      const scrollTop = window.scrollY + el.getBoundingClientRect().top - scrollMarginTop;
      window.scrollTo({ top: scrollTop, behavior: "smooth" });
    };

    const correctScrollAfterLayoutChange = () => {
      const el = document.getElementById("essential-tour-information");
      if (!el) return;

      scrollToSection(el);
    };

    const layoutObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
        if (!hasScrolledToKeyInformation.current || !allowLayoutCorrections) return;

        if (correctionTimeoutId) clearTimeout(correctionTimeoutId);
        correctionTimeoutId = setTimeout(correctScrollAfterLayoutChange, 100);
      })
      : undefined;

    layoutObserver?.observe(document.body);

    window.addEventListener("wheel", stopLayoutCorrections, { passive: true });
    window.addEventListener("touchstart", stopLayoutCorrections, { passive: true });
    window.addEventListener("pointerdown", stopLayoutCorrections, { passive: true });

    const waitForSection = () => {
      const el = document.getElementById("essential-tour-information");
      if (!el) {
        animationFrameId = requestAnimationFrame(waitForSection);
        return;
      }

      hasScrolledToKeyInformation.current = true;
      scrollToSection(el);
    };

    // Scroll if the hash is #what-to-expect, #essential-tour-information, or the
    // legacy #know_before_you_go anchor (still linked from Customer Journey emails
    // during the Sitecore migration). Same-document hash changes (in-page anchor
    // link, URL-bar edit) don't remount this component, so this also runs on
    // "hashchange" - and unlike #what-to-expect/#essential-tour-information,
    // #know_before_you_go has no DOM element of its own, so native fragment
    // scrolling can't cover a same-document change to it either.
    const onHashChange = () => {
      if (
        !["#what-to-expect", "#know_before_you_go", "#essential-tour-information"].includes(
          window.location.hash
        )
      ) {
        return;
      }

      // Restart the poll for the new hash - cancel any in-flight frame first
      // so we never run two polls concurrently.
      cancelAnimationFrame(animationFrameId);
      hasScrolledToKeyInformation.current = false;
      allowLayoutCorrections = true;
      animationFrameId = requestAnimationFrame(waitForSection);
    };

    onHashChange();
    window.addEventListener("hashchange", onHashChange);

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (correctionTimeoutId) clearTimeout(correctionTimeoutId);
      layoutObserver?.disconnect();
      window.removeEventListener("wheel", stopLayoutCorrections);
      window.removeEventListener("touchstart", stopLayoutCorrections);
      window.removeEventListener("pointerdown", stopLayoutCorrections);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  // Show skeleton while loading or when data is not available
  if (isLoading || !keyInformationData) {
    return <KeyInformationSkeleton />;
  }

  // Don't render section if no key information data is available
  if (keyInformationData.length === 0) {
    return null;
  }

  return (
    <Box
      className="fluid-box anchor-section"
      component="section"
      sx={{ padding: { xs: "24px", md: "40px" } }}
      id={resolvedSectionId ?? undefined}
      data-ranking-algorithm="manual"
      data-module-ranking-position="15"      
      data-track="visibility"
      data-parent-container-type="key-information"
      data-module-name="Key Information Section"      
    >
      <SectionHeader
        headerText={t("Essential Tour Information")}
        headerTestId="essential-tour-header-text"
        headerSx={{ textAlign: "center", textTransform: "none !important" }}
        copyText={keyInfoHeader?.copyText ?? ""}
        copyTestId="essential-tour-copy-text"
        copySx={{
          maxWidth: "610px",
          margin: "0 auto",
          fontSize: "1.6rem",
          textAlign: "center",
          color: "text.primary",
          "& p": { margin: "0" },
          "& a": {
            color: "text.primary",
            textDecoration: "underline !important",
            textUnderlineOffset: "2px",
            fontWeight: 700,
            display: "inline"
          }
        }}
      />

      {keyInformationData.map((section: FAQSection, index: number) => (
        <FaqSectionComponent
          key={section.basicInfo?.headerText}
          faqSection={{
            basicInfo: section.basicInfo,
            faqs: section.faqs
          }}
          headerSx={{
            textTransform: "none !important",
            fontSize: { xs: "2rem !important", sm: "2.4rem !important" },
            fontWeight: "600"
          }}
          dynamicId={hrefs[index] || `key-info-section-${index}`}
          index={index}
          faqContainerSx={{ gap: "0rem" }}
          headerComponent="h3"
          accordionComponent="h4"
        />
      ))}
    </Box>
  );
};

export default KeyInformation;
